import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button, Card, CardContent } from "@/ui";
import { CheckCircle, Mail, ArrowLeft, Building, Clock } from "lucide-react";

interface ContactThankYouData {
  name: string;
  email: string;
  organization?: string;
  inquiryType?: string;
  submittedAt: string;
}

export default function ContactThankYou() {
  const [, setLocation] = useLocation();
  const [contactData, setContactData] = useState<ContactThankYouData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get data from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const name = urlParams.get('name');
    const email = urlParams.get('email');
    const organization = urlParams.get('organization');
    const inquiryType = urlParams.get('inquiryType');
    const submittedAt = urlParams.get('submittedAt');

    if (name && email) {
      setContactData({
        name: decodeURIComponent(name),
        email: decodeURIComponent(email),
        organization: organization ? decodeURIComponent(organization) : undefined,
        inquiryType: inquiryType ? decodeURIComponent(inquiryType) : undefined,
        submittedAt: submittedAt ? decodeURIComponent(submittedAt) : new Date().toISOString()
      });
    }
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div>
        <main className="py-20">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading...</p>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  if (!contactData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <main className="py-20">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="shadow-xl border-2">
              <CardContent className="pt-6 text-center">
                <div className="text-blue-500 mb-4">
                  <CheckCircle className="h-16 w-16 mx-auto" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-4">Contact Request Not Found</h1>
                <p className="text-muted-foreground mb-6">
                  We couldn't find your contact request information. This might happen if you accessed this page directly.
                </p>
                <div className="flex justify-center gap-4">
                  <Button onClick={() => setLocation('/contact')}>
                    Submit Contact Request
                  </Button>
                  <Button variant="outline" onClick={() => setLocation('/')}>
                    Go Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      
      <main className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Success Message */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Thank You, {contactData.name}!
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Your request for information has been successfully submitted. We appreciate your interest in AIscendus Healthcare Solutions.
            </p>
          </div>

          {/* Contact Details Card */}
          <Card className="shadow-xl border-2 mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
                Request Details
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Full Name</label>
                    <p className="text-lg font-semibold text-foreground">{contactData.name}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email Address</label>
                    <p className="text-lg text-foreground">{contactData.email}</p>
                  </div>
                  
                  {contactData.organization && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Organization</label>
                      <p className="text-lg text-foreground">{contactData.organization}</p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  {contactData.inquiryType && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Inquiry Type</label>
                      <p className="text-lg text-foreground capitalize">{contactData.inquiryType.replace('-', ' ')}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Submitted At</label>
                    <p className="text-lg text-foreground">{new Date(contactData.submittedAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What's Next Section */}
          <Card className="shadow-xl border-2 mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
                What Happens Next?
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex flex-col items-center text-center p-4 bg-muted/30 rounded-lg">
                  <Clock className="h-10 w-10 text-blue-600 mb-3" />
                  <p className="font-semibold text-foreground mb-2">Review</p>
                  <p className="text-sm text-muted-foreground">Our team will review your request within 24 hours.</p>
                </div>
                
                <div className="flex flex-col items-center text-center p-4 bg-muted/30 rounded-lg">
                  <Mail className="h-10 w-10 text-green-600 mb-3" />
                  <p className="font-semibold text-foreground mb-2">Contact</p>
                  <p className="text-sm text-muted-foreground">We'll reach out via email with next steps.</p>
                </div>
                
                <div className="flex flex-col items-center text-center p-4 bg-muted/30 rounded-lg">
                  <Building className="h-10 w-10 text-purple-600 mb-3" />
                  <p className="font-semibold text-foreground mb-2">Engage</p>
                  <p className="text-sm text-muted-foreground">Schedule a personalized demo or discussion.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information Card */}
          <Card className="shadow-xl border-2 mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
                Need Immediate Assistance?
              </h2>
              
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mb-4 mx-auto">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Email Us</h3>
                <p className="text-muted-foreground mb-2">contact@aiscendus.com</p>
                <p className="text-sm text-muted-foreground">Response within 24 hours</p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              onClick={() => setLocation('/')} 
              className="flex items-center gap-2"
              size="lg"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setLocation('/contact')} 
              className="flex items-center gap-2"
              size="lg"
            >
              Submit Another Request
            </Button>
          </div>
        </div>
      </main>
      
    </div>
  );
}
